import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaAlignLeft as FaAlignLeftRaw,
  FaCheck as FaCheckRaw,
  FaChevronRight as FaChevronRightRaw,
  FaCloudUploadAlt as FaCloudUploadAltRaw,
  FaCog as FaCogRaw,
  FaEquals as FaEqualsRaw,
  FaEye as FaEyeRaw,
  FaFileAlt as FaFileAltRaw,
  FaLink as FaLinkRaw,
  FaListUl as FaListUlRaw,
  FaTrashAlt as FaTrashAltRaw,
} from 'react-icons/fa';
import { teacherService } from '../../services/classroom/teacherService';
import {
  type AssignmentBuilderSettings,
  defaultQuestion,
  parseWorkDescription,
  questionTypeLabel,
  serializeWorkDescription,
  type WorkQuestion,
  type WorkQuestionType,
} from './assignmentWorkContent';

export type WorkEditorConfig = {
  workType: 'assignment' | 'quiz';
  /** URL param name for edit mode */
  routeParam: 'assignmentId' | 'quizId';
  listPath: string;
  editPath: (id: string) => string;
  copy: {
    sideHeading: string;
    stepInfo: string;
    stepQuestions: string;
    stepSettings: string;
    stepPreview: string;
    infoTitle: string;
    infoSubtitle: string;
    questionsTitle: string;
    questionsSubtitle: string;
    settingsTitle: string;
    settingsSubtitle: string;
    previewTitle: string;
    previewSubtitle: string;
    titlePlaceholder: string;
    untitled: string;
    notFound: string;
    noClasses: string;
  };
};

export const assignmentWorkEditorConfig: WorkEditorConfig = {
  workType: 'assignment',
  routeParam: 'assignmentId',
  listPath: '/classroom-teacher/assignments',
  editPath: (id) => `/classroom-teacher/assignments/${id}/edit`,
  copy: {
    sideHeading: 'ASSIGNMENT BUILDER',
    stepInfo: 'Assignment Info',
    stepQuestions: 'Questions',
    stepSettings: 'Settings',
    stepPreview: 'Preview',
    infoTitle: 'Assignment details',
    infoSubtitle: 'Set the class, title, schedule, and instructions students see first.',
    questionsTitle: 'Build assignment questions',
    questionsSubtitle: 'Create interactive assessments for your students.',
    settingsTitle: 'Assignment settings',
    settingsSubtitle: 'Control how this assignment behaves when students take it.',
    previewTitle: 'Preview',
    previewSubtitle: 'Review everything before saving.',
    titlePlaceholder: 'Assignment title',
    untitled: 'Untitled assignment',
    notFound: 'Assignment not found.',
    noClasses: 'Create a class before building assignments.',
  },
};

export const quizWorkEditorConfig: WorkEditorConfig = {
  workType: 'quiz',
  routeParam: 'quizId',
  listPath: '/classroom-teacher/quizzes',
  editPath: (id) => `/classroom-teacher/quizzes/${id}/edit`,
  copy: {
    sideHeading: 'QUIZ BUILDER',
    stepInfo: 'Quiz Info',
    stepQuestions: 'Questions',
    stepSettings: 'Settings',
    stepPreview: 'Preview',
    infoTitle: 'Quiz details',
    infoSubtitle: 'Set the class, title, schedule, and instructions students see before starting the quiz.',
    questionsTitle: 'Build quiz questions',
    questionsSubtitle: 'Create interactive questions for your quiz.',
    settingsTitle: 'Quiz settings',
    settingsSubtitle: 'Control how this quiz behaves when students take it.',
    previewTitle: 'Preview',
    previewSubtitle: 'Review everything before saving.',
    titlePlaceholder: 'Quiz title',
    untitled: 'Untitled quiz',
    notFound: 'Quiz not found.',
    noClasses: 'Create a class before building quizzes.',
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaAlignLeft: any = FaAlignLeftRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCheck: any = FaCheckRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChevronRight: any = FaChevronRightRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCloudUploadAlt: any = FaCloudUploadAltRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCog: any = FaCogRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEquals: any = FaEqualsRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEye: any = FaEyeRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFileAlt: any = FaFileAltRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaLink: any = FaLinkRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaListUl: any = FaListUlRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaTrashAlt: any = FaTrashAltRaw;

const MOBILE_BREAKPOINT = '1100px';

const PageWrap = styled.div`
  width: 100%;
  min-width: 0;
  padding-bottom: 5.75rem;
`;

const BuilderHeader = styled.div`
  margin-bottom: 1.25rem;
`;

const StepKicker = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--primary);
  margin-bottom: 0.35rem;
`;

const BuilderTitle = styled.h2`
  margin: 0 0 0.4rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.25rem, 2vw, 1.55rem);
  font-weight: 700;
  color: var(--text);
`;

const BuilderSubtitle = styled.p`
  margin: 0;
  font-size: 0.92rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.45;
  max-width: 40rem;
`;

const BuilderGrid = styled.div<{ $threeCol: boolean }>`
  display: grid;
  grid-template-columns: ${({ $threeCol }) =>
    $threeCol ? '210px minmax(0, 1fr) 270px' : '210px minmax(0, 1fr)'};
  gap: 1.35rem;
  align-items: start;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

const SideColumn = styled.aside`
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    order: -1;
  }
`;

const SideHeading = styled.div`
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-bottom: 0.35rem;
`;

const DraftBadge = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 1rem;
`;

const StepList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const StepRow = styled.li<{ $active?: boolean; $done?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? 'var(--primary)' : 'transparent')};
  background: ${({ $active }) => ($active ? 'var(--primaryContainer, rgba(25, 118, 210, 0.08))' : 'transparent')};
  color: var(--text);
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: var(--surfaceVariant);
  }
`;

const StepIconWrap = styled.span<{ $active?: boolean; $done?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $active, $done }) =>
    $done ? 'var(--primary)' : $active ? 'var(--primary)' : 'var(--surfaceVariant)'};
  color: ${({ $done, $active }) => ($done || $active ? 'var(--onPrimary, #fff)' : 'var(--textSecondary, var(--onSurfaceVariant))')};
  font-size: 0.85rem;
`;

const StepLabel = styled.div`
  font-size: 0.82rem;
  font-weight: ${600};
  line-height: 1.25;
`;

const MainColumn = styled.main`
  min-width: 0;
`;

const Card = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1.15rem 1.2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.95rem;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  label {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--textSecondary, var(--onSurfaceVariant));
  }
  input,
  select,
  textarea {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    padding: 0.55rem 0.7rem;
    border-radius: 10px;
    border: 1px solid var(--outline);
    font-family: 'Open Sans', sans-serif;
    font-size: 0.9rem;
    color: var(--text);
    background: var(--surface);
  }
`;

const QuestionCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1rem 1.1rem;
  margin-bottom: 0.85rem;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.05);
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.85rem;
    border-radius: 10px;
  }
`;

const QHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
`;

const QHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
`;

const QIndex = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--surfaceVariant);
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const QType = styled.span`
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const QTrash = styled.button`
  border: none;
  background: transparent;
  color: var(--textSecondary, var(--onSurfaceVariant));
  cursor: pointer;
  padding: 0.35rem;
  border-radius: 8px;
  &:hover {
    color: var(--error, #c62828);
    background: var(--surfaceVariant);
  }
`;

const PromptLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-bottom: 0.35rem;
`;

const PromptText = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  min-height: 2.6rem;
  padding: 0.55rem 0.7rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-family: 'Open Sans', sans-serif;
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--text);
  resize: vertical;
  margin-bottom: 0.65rem;
  background: var(--surface);
`;

const PointsRow = styled.div`
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PointsLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const McOptionRow = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.65rem;
  border-radius: 10px;
  border: 1px solid ${({ $selected }) => ($selected ? 'var(--primary)' : 'var(--outline)')};
  margin-bottom: 0.45rem;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? 'var(--primaryContainer, rgba(25, 118, 210, 0.06))' : 'var(--surface)')};
  input {
    accent-color: var(--primary);
  }
`;

const McOptionInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 0.88rem;
  color: var(--text);
  outline: none;
  font-family: 'Open Sans', sans-serif;
`;

const CorrectBadge = styled.span`
  margin-left: auto;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: var(--primary);
  white-space: nowrap;
`;

const LibraryColumn = styled.aside`
  min-width: 0;
  /* Stretch to the full questions-column height so sticky has room to travel.
     With align-items:start on the grid, a short aside makes sticky a no-op. */
  align-self: stretch;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    align-self: auto;
  }
`;

const LibrarySection = styled.div`
  position: sticky;
  top: 0.5rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    position: static;
  }
`;

const LibraryTitle = styled.div`
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-bottom: 0.65rem;
`;

const LibraryCard = styled.button<{ $accent: 'blue' | 'orange' | 'purple' | 'green' | 'red' }>`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  width: 100%;
  text-align: left;
  padding: 0.75rem 0.85rem;
  margin-bottom: 0.5rem;
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: var(--surface);
  cursor: pointer;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.04);
  &:hover {
    border-color: var(--primary);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  }
`;

const LibIcon = styled.span<{ $accent: 'blue' | 'orange' | 'purple' | 'green' | 'red' }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  background: ${({ $accent }) =>
    $accent === 'blue'
      ? '#2563eb'
      : $accent === 'orange'
        ? '#ea580c'
        : $accent === 'purple'
          ? '#7c3aed'
          : $accent === 'green'
            ? '#16a34a'
            : '#dc2626'};
`;

const LibTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text);
`;

const LibSub = styled.div`
  font-size: 0.68rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-top: 0.15rem;
  line-height: 1.35;
`;

const TipCard = styled.div`
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: var(--primaryContainer, rgba(25, 118, 210, 0.1));
  border: 1px solid var(--outline);
  font-size: 0.78rem;
  line-height: 1.45;
  color: var(--text);
`;

const TipStrong = styled.strong`
  display: block;
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  color: var(--primary);
  margin-bottom: 0.35rem;
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid var(--outline);
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
  &:last-child {
    border-bottom: none;
  }
  input {
    width: 18px;
    height: 18px;
    accent-color: var(--primary);
  }
`;

const PreviewBlock = styled.div`
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--text);
  h4 {
    margin: 0.75rem 0 0.35rem;
    font-size: 0.85rem;
  }
  ul {
    margin: 0;
    padding-left: 1.1rem;
  }
`;

const StickyFooter = styled.footer`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 40;
  background: var(--surface);
  border-top: 1px solid var(--outline);
  padding: 0.85rem 1.25rem;
  box-shadow: 0 -6px 24px rgba(0, 0, 0, 0.06);
`;

const FooterInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const GhostBtn = styled.button`
  border: none;
  background: none;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--textSecondary, var(--onSurfaceVariant));
  cursor: pointer;
  font-family: 'Open Sans', sans-serif;
  &:hover {
    color: var(--primary);
  }
`;

const SecondaryBtn = styled.button`
  padding: 0.55rem 1.1rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  color: var(--text);
  cursor: pointer;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 1.15rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  border: none;
  border-radius: 10px;
  background: var(--primary);
  color: var(--onPrimary);
  cursor: pointer;
  &:hover {
    background: var(--primaryDark);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PointsInput = styled.input`
  box-sizing: border-box;
  width: 88px;
  min-width: 0;
  padding: 0.45rem 0.55rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  font-size: 0.85rem;
`;

const RubricTextArea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 0.55rem 0.7rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-family: 'Open Sans', sans-serif;
  font-size: 0.88rem;
  color: var(--text);
  background: var(--surface);
`;

const PairList = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const PairRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 0.45rem;
  align-items: center;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

const PairInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 0.45rem 0.55rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  font-size: 0.88rem;
  color: var(--text);
  background: var(--surface);
`;

const QuestionActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.35rem;
  flex-wrap: wrap;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
  margin-left: auto;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    margin-left: 0;
    > button {
      flex: 1 1 0;
      min-width: 0;
      justify-content: center;
    }
  }
`;

type StepKey = 'info' | 'questions' | 'settings' | 'preview';

function buildSteps(config: WorkEditorConfig): { key: StepKey; label: string }[] {
  return [
    { key: 'info', label: config.copy.stepInfo },
    { key: 'questions', label: config.copy.stepQuestions },
    { key: 'settings', label: config.copy.stepSettings },
    { key: 'preview', label: config.copy.stepPreview },
  ];
}

export type ClassroomTeacherWorkEditorPageProps = {
  config: WorkEditorConfig;
};

export const ClassroomTeacherWorkEditorPage: React.FC<ClassroomTeacherWorkEditorPageProps> = ({ config }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeParams = useParams<Record<string, string | undefined>>();
  const workId = routeParams[config.routeParam];
  const isNew = !workId;
  const queryClient = useQueryClient();

  const [step, setStep] = useState<StepKey>('info');
  const [newClassId, setNewClassId] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formDue, setFormDue] = useState('');
  const [formPoints, setFormPoints] = useState(10);
  const [formInstructions, setFormInstructions] = useState('');
  const [formQuestions, setFormQuestions] = useState<WorkQuestion[]>([]);
  const [builderSettings, setBuilderSettings] = useState<AssignmentBuilderSettings>({
    shuffleQuestions: false,
    allowLateSubmission: true,
  });
  const [hydrated, setHydrated] = useState(isNew);

  const workQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'work', config.workType],
    queryFn: () => teacherService.getWork(config.workType),
  });

  const overviewQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'overview'],
    queryFn: teacherService.getOverview,
  });

  const classOptions = useMemo(() => overviewQuery.data ?? [], [overviewQuery.data]);

  useEffect(() => {
    if (!isNew) return;
    const cid = searchParams.get('classId')?.trim();
    if (!cid) return;
    if (!classOptions.some((row) => row.id === cid)) return;
    setNewClassId(cid);
  }, [isNew, searchParams, classOptions]);

  const existing = useMemo(
    () => (workId ? (workQuery.data ?? []).find((r) => r.id === workId) : undefined),
    [workId, workQuery.data]
  );

  useEffect(() => {
    if (newClassId) return;
    const first = classOptions[0]?.id;
    if (first) setNewClassId(first);
  }, [classOptions, newClassId]);

  useEffect(() => {
    if (isNew) {
      setHydrated(true);
      return;
    }
    if (!workId || workQuery.isLoading) return;
    if (!existing) {
      setHydrated(true);
      return;
    }
    const parsed = parseWorkDescription(existing.description);
    setNewClassId(existing.class_id);
    setFormTitle(existing.title);
    setFormDue(existing.due_at ? existing.due_at.slice(0, 16) : '');
    setFormPoints(existing.points);
    setFormInstructions(parsed.instructions);
    setFormQuestions(parsed.questions);
    setBuilderSettings(parsed.settings);
    setHydrated(true);
  }, [isNew, workId, existing, workQuery.isLoading]);

  const saveMutation = useMutation({
    mutationFn: teacherService.saveWork,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'work', config.workType] });
    },
  });

  const steps = useMemo(() => buildSteps(config), [config]);
  const stepIndex = steps.findIndex((s) => s.key === step);

  const step1Done = Boolean(newClassId && formTitle.trim());
  const step2Done = formQuestions.length > 0;
  const step3Done = true;

  const addQuestion = (questionType: WorkQuestionType) => {
    setFormQuestions((prev) => [...prev, defaultQuestion(questionType)]);
  };

  const updateQuestion = (questionId: string, patch: Partial<WorkQuestion>) => {
    setFormQuestions((prev) => prev.map((q) => (q.id === questionId ? { ...q, ...patch } : q)));
  };

  const removeQuestion = (questionId: string) => {
    setFormQuestions((prev) => prev.filter((q) => q.id !== questionId));
  };

  const updateOption = (questionId: string, optionIdx: number, value: string) => {
    setFormQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId
          ? { ...q, options: q.options.map((opt, idx) => (idx === optionIdx ? value : opt)) }
          : q
      )
    );
  };

  const addOption = (questionId: string) => {
    setFormQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId ? { ...q, options: [...q.options, `Option ${q.options.length + 1}`] } : q
      )
    );
  };

  const removeOption = (questionId: string, optionIdx: number) => {
    setFormQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId
          ? { ...q, options: q.options.filter((_, idx) => idx !== optionIdx), answer: '' }
          : q
      )
    );
  };

  const updatePair = (questionId: string, pairIdx: number, side: 'left' | 'right', value: string) => {
    setFormQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== questionId || !q.pairs) return q;
        const pairs = q.pairs.map((p, i) => (i === pairIdx ? { ...p, [side]: value } : p));
        return { ...q, pairs };
      })
    );
  };

  const addPair = (questionId: string) => {
    setFormQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId && q.pairs ? { ...q, pairs: [...q.pairs, { left: '', right: '' }] } : q
      )
    );
  };

  const removePair = (questionId: string, pairIdx: number) => {
    setFormQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== questionId || !q.pairs) return q;
        const pairs = q.pairs.filter((_, i) => i !== pairIdx);
        return { ...q, pairs: pairs.length ? pairs : [{ left: '', right: '' }] };
      })
    );
  };

  const buildPayload = useCallback(
    (dueAt: string | null) => {
      const description = serializeWorkDescription(formInstructions, formQuestions, builderSettings);
      return {
        id: isNew ? undefined : workId,
        class_id: newClassId || classOptions[0]?.id || '',
        title: formTitle.trim() || config.copy.untitled,
        description,
        due_at: dueAt,
        points: formPoints,
        archived: false,
        type: config.workType,
      };
    },
    [
      workId,
      builderSettings,
      classOptions,
      config.copy.untitled,
      config.workType,
      formInstructions,
      formPoints,
      formQuestions,
      formTitle,
      isNew,
      newClassId,
    ]
  );

  const saveDraft = async () => {
    try {
      const res = await saveMutation.mutateAsync(buildPayload(null));
      if (res.ok && isNew && res.data?.id) {
        navigate(config.editPath(res.data.id), { replace: true });
      }
    } catch {
      /* mutation surfaces error state via isError if needed */
    }
  };

  const saveAndFinish = async () => {
    const due = formDue ? new Date(formDue).toISOString() : null;
    try {
      const res = await saveMutation.mutateAsync(buildPayload(due));
      if (res.ok) navigate(config.listPath);
    } catch {
      /* keep user on page */
    }
  };

  const onPrimaryFooter = () => {
    if (step === 'preview') {
      void saveAndFinish();
      return;
    }
    const next = steps[stepIndex + 1];
    if (next) setStep(next.key);
  };

  const footerPrimaryLabel =
    step === 'info'
      ? 'Next: Questions'
      : step === 'questions'
        ? 'Next: Settings'
        : step === 'settings'
          ? 'Next: Preview'
          : 'Save & exit';

  if (overviewQuery.isLoading || workQuery.isLoading || !hydrated) {
    return <p style={{ color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>Loading…</p>;
  }

  if (!isNew && workId && workQuery.data && !existing) {
    return <p style={{ color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>{config.copy.notFound}</p>;
  }

  if (classOptions.length === 0) {
    return (
      <p style={{ color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>
        {config.copy.noClasses}
      </p>
    );
  }

  return (
    <PageWrap>
      <BuilderHeader>
        <StepKicker>
          STEP {stepIndex + 1} OF {steps.length}
        </StepKicker>
        <BuilderTitle>
          {step === 'info' && config.copy.infoTitle}
          {step === 'questions' && config.copy.questionsTitle}
          {step === 'settings' && config.copy.settingsTitle}
          {step === 'preview' && config.copy.previewTitle}
        </BuilderTitle>
        <BuilderSubtitle>
          {step === 'info' && config.copy.infoSubtitle}
          {step === 'questions' && config.copy.questionsSubtitle}
          {step === 'settings' && config.copy.settingsSubtitle}
          {step === 'preview' && config.copy.previewSubtitle}
        </BuilderSubtitle>
      </BuilderHeader>

      <BuilderGrid $threeCol={step === 'questions'}>
        <SideColumn>
          <SideHeading>{config.copy.sideHeading}</SideHeading>
          <DraftBadge>Draft mode</DraftBadge>
          <StepList>
            {steps.map((s) => {
              const done =
                (s.key === 'info' && step1Done) ||
                (s.key === 'questions' && step2Done) ||
                (s.key === 'settings' && step3Done);
              const active = step === s.key;
              const showCheck = done && !active;
              const StepGlyph =
                s.key === 'info' ? (
                  <FaFileAlt size={14} />
                ) : s.key === 'questions' ? (
                  <FaFileAlt size={14} />
                ) : s.key === 'settings' ? (
                  <FaCog size={14} />
                ) : (
                  <FaEye size={14} />
                );
              return (
                <StepRow
                  key={s.key}
                  $active={active}
                  $done={showCheck}
                  onClick={() => setStep(s.key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setStep(s.key);
                    }
                  }}
                >
                  <StepIconWrap $active={active} $done={showCheck}>
                    {showCheck ? <FaCheck size={14} /> : StepGlyph}
                  </StepIconWrap>
                  <StepLabel>{s.label}</StepLabel>
                </StepRow>
              );
            })}
          </StepList>
        </SideColumn>

        <MainColumn>
          {step === 'info' ? (
            <Card>
              <FieldGrid>
                <label>
                  Class
                  <select
                    value={newClassId}
                    onChange={(e) => setNewClassId(e.target.value)}
                    disabled={!isNew}
                  >
                    {classOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Title
                  <input
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder={config.copy.titlePlaceholder}
                  />
                </label>
                <label>
                  Due date
                  <input type="datetime-local" value={formDue} onChange={(e) => setFormDue(e.target.value)} />
                </label>
                <label>
                  Total points
                  <input
                    type="number"
                    min={1}
                    value={formPoints}
                    onChange={(e) => setFormPoints(Number(e.target.value))}
                  />
                </label>
                <label>
                  Instructions
                  <textarea
                    value={formInstructions}
                    onChange={(e) => setFormInstructions(e.target.value)}
                    rows={4}
                    placeholder="Instructions students should read before starting."
                  />
                </label>
              </FieldGrid>
            </Card>
          ) : null}

          {step === 'questions' ? (
            <>
              {formQuestions.length === 0 ? (
                <Card style={{ marginBottom: '1rem', color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>
                  No questions yet. Click a type in the library to add one.
                </Card>
              ) : null}
              {formQuestions.map((question, qIdx) => (
                <QuestionCard key={question.id}>
                  <QHeader>
                    <QHeaderLeft>
                      <QIndex>{qIdx + 1}</QIndex>
                      <QType>{questionTypeLabel(question.type)}</QType>
                    </QHeaderLeft>
                    <QTrash type="button" aria-label="Remove question" onClick={() => removeQuestion(question.id)}>
                      <FaTrashAlt size={14} />
                    </QTrash>
                  </QHeader>
                  <PromptLabel>Question text</PromptLabel>
                  <PromptText
                    value={question.prompt}
                    onChange={(e) => updateQuestion(question.id, { prompt: e.target.value })}
                    placeholder="Enter the question"
                  />
                  <PointsRow>
                    <PointsLabel>Points</PointsLabel>
                    <PointsInput
                      type="number"
                      min={1}
                      value={question.points}
                      onChange={(e) =>
                        updateQuestion(question.id, { points: Math.max(1, Number(e.target.value) || 1) })
                      }
                    />
                  </PointsRow>

                  {question.type === 'multiple_choice' || question.type === 'true_false' ? (
                    <div>
                      {question.options.map((option, optionIdx) => (
                        <McOptionRow
                          key={`${question.id}-opt-${optionIdx}`}
                          $selected={question.answer === option}
                        >
                          <input
                            type="radio"
                            name={`correct-${question.id}`}
                            checked={question.answer === option}
                            onChange={() => updateQuestion(question.id, { answer: option })}
                          />
                          <McOptionInput
                            value={option}
                            onChange={(e) => updateOption(question.id, optionIdx, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          {question.answer === option ? <CorrectBadge>✓ CORRECT</CorrectBadge> : null}
                        </McOptionRow>
                      ))}
                      {question.type === 'multiple_choice' ? (
                        <QuestionActions>
                          <SecondaryBtn type="button" onClick={() => addOption(question.id)}>
                            Add option
                          </SecondaryBtn>
                          <SecondaryBtn
                            type="button"
                            onClick={() => removeOption(question.id, question.options.length - 1)}
                            disabled={question.options.length <= 2}
                          >
                            Remove last option
                          </SecondaryBtn>
                        </QuestionActions>
                      ) : null}
                    </div>
                  ) : null}

                  {question.type === 'long_form' || question.type === 'short_answer' || question.type === 'file_upload' ? (
                    <RubricTextArea
                      value={question.answer}
                      onChange={(e) => updateQuestion(question.id, { answer: e.target.value })}
                      rows={question.type === 'short_answer' ? 2 : 4}
                      placeholder={
                        question.type === 'file_upload'
                          ? 'Rubric or expected deliverable (e.g. PDF, one photo)'
                          : 'Expected answer or rubric guidance'
                      }
                    />
                  ) : null}

                  {question.type === 'matching' && question.pairs ? (
                    <PairList>
                      {question.pairs.map((pair, pIdx) => (
                        <PairRow key={`${question.id}-p-${pIdx}`}>
                          <PairInput
                            value={pair.left}
                            onChange={(e) => updatePair(question.id, pIdx, 'left', e.target.value)}
                            placeholder="Left item"
                          />
                          <PairInput
                            value={pair.right}
                            onChange={(e) => updatePair(question.id, pIdx, 'right', e.target.value)}
                            placeholder="Right item"
                          />
                          <SecondaryBtn type="button" onClick={() => removePair(question.id, pIdx)}>
                            Remove
                          </SecondaryBtn>
                        </PairRow>
                      ))}
                      <SecondaryBtn type="button" onClick={() => addPair(question.id)}>
                        Add pair
                      </SecondaryBtn>
                    </PairList>
                  ) : null}
                </QuestionCard>
              ))}
            </>
          ) : null}

          {step === 'settings' ? (
            <Card>
              <ToggleRow>
                <input
                  type="checkbox"
                  checked={builderSettings.shuffleQuestions}
                  onChange={(e) =>
                    setBuilderSettings((s) => ({ ...s, shuffleQuestions: e.target.checked }))
                  }
                />
                Shuffle question order for each student
              </ToggleRow>
              <ToggleRow>
                <input
                  type="checkbox"
                  checked={builderSettings.allowLateSubmission}
                  onChange={(e) =>
                    setBuilderSettings((s) => ({ ...s, allowLateSubmission: e.target.checked }))
                  }
                />
                Allow late submissions
              </ToggleRow>
            </Card>
          ) : null}

          {step === 'preview' ? (
            <Card>
              <PreviewBlock>
                <strong>{formTitle.trim() || config.copy.untitled}</strong>
                <div style={{ color: 'var(--textSecondary)', marginTop: '0.35rem', fontSize: '0.85rem' }}>
                  {formPoints} points
                  {formDue ? ` · Due ${new Date(formDue).toLocaleString()}` : ' · Draft (no due date)'}
                </div>
                {formInstructions.trim() ? (
                  <>
                    <h4>Instructions</h4>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{formInstructions}</div>
                  </>
                ) : null}
                <h4>Questions ({formQuestions.length})</h4>
                <ul>
                  {formQuestions.map((q, i) => (
                    <li key={q.id}>
                      {i + 1}. {questionTypeLabel(q.type)}
                      {q.prompt ? ` — ${q.prompt.slice(0, 80)}${q.prompt.length > 80 ? '…' : ''}` : ''}
                    </li>
                  ))}
                </ul>
              </PreviewBlock>
            </Card>
          ) : null}
        </MainColumn>

        {step === 'questions' ? (
          <LibraryColumn>
            <LibrarySection>
              <LibraryTitle>DRAG OR CLICK TO ADD</LibraryTitle>
              <LibraryCard type="button" $accent="blue" onClick={() => addQuestion('multiple_choice')}>
                <LibIcon $accent="blue">
                  <FaListUl size={16} />
                </LibIcon>
                <div>
                  <LibTitle>Multiple choice</LibTitle>
                  <LibSub>Single or multi-select style prompts; mark the correct option.</LibSub>
                </div>
              </LibraryCard>
              <LibraryCard type="button" $accent="orange" onClick={() => addQuestion('short_answer')}>
                <LibIcon $accent="orange">
                  <FaAlignLeft size={16} />
                </LibIcon>
                <div>
                  <LibTitle>Short answer</LibTitle>
                  <LibSub>Open text response with a brief expected answer or rubric.</LibSub>
                </div>
              </LibraryCard>
              <LibraryCard type="button" $accent="purple" onClick={() => addQuestion('matching')}>
                <LibIcon $accent="purple">
                  <FaLink size={16} />
                </LibIcon>
                <div>
                  <LibTitle>Matching</LibTitle>
                  <LibSub>Connect pairs of terms or definitions.</LibSub>
                </div>
              </LibraryCard>
              <LibraryCard type="button" $accent="green" onClick={() => addQuestion('file_upload')}>
                <LibIcon $accent="green">
                  <FaCloudUploadAlt size={16} />
                </LibIcon>
                <div>
                  <LibTitle>File upload</LibTitle>
                  <LibSub>Students submit documents or images.</LibSub>
                </div>
              </LibraryCard>
              <LibraryCard type="button" $accent="red" onClick={() => addQuestion('true_false')}>
                <LibIcon $accent="red">
                  <FaEquals size={16} />
                </LibIcon>
                <div>
                  <LibTitle>True / False</LibTitle>
                  <LibSub>Binary choice question.</LibSub>
                </div>
              </LibraryCard>
              <TipCard>
                <TipStrong>SCHOLAR TIP</TipStrong>
                Mixed assessment formats improve student engagement and test multiple cognitive dimensions.
              </TipCard>
            </LibrarySection>
          </LibraryColumn>
        ) : null}
      </BuilderGrid>

      <StickyFooter>
        <FooterInner>
          <GhostBtn type="button" onClick={() => navigate(config.listPath)}>
            Cancel
          </GhostBtn>
          <FooterActions>
            <SecondaryBtn type="button" onClick={() => void saveDraft()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : 'Save draft'}
            </SecondaryBtn>
            <PrimaryBtn type="button" onClick={onPrimaryFooter} disabled={saveMutation.isPending}>
              {footerPrimaryLabel}
              <FaChevronRight size={12} aria-hidden />
            </PrimaryBtn>
          </FooterActions>
        </FooterInner>
      </StickyFooter>
    </PageWrap>
  );
};
